import React, { useEffect, useRef, useState } from "react";

import tradeTraining from "./images/kard1.jpg";
import practicalSkills from "./images/kard2.jpg";

const vocationCards = [
  {
    id: 1,
    image: tradeTraining,
    number: "01",
    eyebrow: "Hands-On Learning",
    title: "Practical Trade Training",
    description:
      "Students gain practical, hands-on experience through technical training, workshops, demonstrations, and real-world projects. Our approach helps learners develop valuable skills they can confidently apply in the workplace or in their own businesses.",
  },
  {
    id: 2,
    image: practicalSkills,
    number: "02",
    eyebrow: "Career Development",
    title: "Employment & Entrepreneurship",
    description:
      "We prepare students for productive careers by combining technical knowledge with creativity, problem-solving, professional discipline, and entrepreneurial thinking. Our goal is to help every learner turn practical skills into opportunity.",
  },
];

const Card = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.15,
      }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        /* =========================================================
           VOCATION / TRADE SCHOOL SECTION
           GREEN + BLACK + YELLOW
        ========================================================= */

        .trade-section {
          position: relative;

          width: 100%;

          padding: 105px 20px 115px;

          overflow: hidden;

          background:
            linear-gradient(
              180deg,
              #ffffff 0%,
              #f8faf8 100%
            );

          font-family:
            "Poppins",
            "Segoe UI",
            Arial,
            sans-serif;
        }

        /* =========================================================
           BACKGROUND DECORATION
        ========================================================= */

        .trade-background-circle {
          position: absolute;

          width: 430px;
          height: 430px;

          border-radius: 50%;

          background:
            rgba(8, 127, 35, 0.035);

          top: -180px;
          right: -140px;

          pointer-events: none;
        }

        .trade-background-circle-bottom {
          position: absolute;

          width: 330px;
          height: 330px;

          border-radius: 50%;

          background:
            rgba(244, 196, 0, 0.045);

          bottom: -180px;
          left: -130px;

          pointer-events: none;
        }

        .trade-container {
          position: relative;

          z-index: 2;

          width:
            min(1240px, 100%);

          margin: 0 auto;
        }

        /* =========================================================
           HEADER
        ========================================================= */

        .trade-header {
          max-width: 820px;

          margin:
            0 auto 65px;

          text-align: center;

          opacity: 0;

          transform:
            translateY(35px);

          transition:
            opacity 0.8s ease,
            transform 0.8s ease;
        }

        .trade-header.is-visible {
          opacity: 1;

          transform:
            translateY(0);
        }

        .trade-label {
          position: relative;

          display: inline-flex;
          align-items: center;
          gap: 9px;

          padding:
            8px 17px;

          margin-bottom: 20px;

          color:
            #087f23;

          background:
            rgba(8, 127, 35, 0.065);

          border:
            1px solid
            rgba(8, 127, 35, 0.15);

          border-radius: 40px;

          font-size: 10px;
          font-weight: 800;

          letter-spacing: 1.7px;

          text-transform: uppercase;
        }

        .trade-label::before {
          content: "";

          width: 7px;
          height: 7px;

          background:
            #f4c400;

          border-radius: 50%;

          box-shadow:
            0 0 0 4px
            rgba(244, 196, 0, 0.13);

          animation:
            tradePulse 2s infinite;
        }

        @keyframes tradePulse {
          0%,
          100% {
            box-shadow:
              0 0 0 4px
              rgba(244, 196, 0, 0.13);
          }

          50% {
            box-shadow:
              0 0 0 8px
              rgba(244, 196, 0, 0.02);
          }
        }

        .trade-heading {
          margin: 0;

          color:
            #111111;

          font-size:
            clamp(32px, 4.5vw, 52px);

          font-weight: 800;

          line-height: 1.08;

          letter-spacing:
            -1.7px;
        }

        .trade-heading span {
          display: block;

          margin-top: 8px;

          color:
            #087f23;
        }

        .trade-heading-description {
          max-width: 730px;

          margin:
            22px auto 0;

          color:
            #687278;

          font-size: 14px;

          line-height: 1.85;
        }

        /* =========================================================
           CARDS
        ========================================================= */

        .trade-grid {
          display: grid;

          grid-template-columns:
            repeat(2, minmax(0, 1fr));

          gap: 35px;
        }

        .trade-card {
          position: relative;

          min-width: 0;

          overflow: hidden;

          background:
            #ffffff;

          border:
            1px solid
            #e5e9e6;

          border-radius:
            20px;

          box-shadow:
            0 10px 35px
            rgba(0, 0, 0, 0.055);

          opacity: 0;

          transform:
            translateY(65px)
            scale(0.97);

          transition:
            opacity 0.75s ease,
            transform 0.75s
              cubic-bezier(0.22, 1, 0.36, 1),
            box-shadow 0.35s ease,
            border-color 0.35s ease;
        }

        .trade-card:nth-child(1) {
          transition-delay:
            0.12s;
        }

        .trade-card:nth-child(2) {
          transition-delay:
            0.25s;
        }

        .trade-card.is-visible {
          opacity: 1;

          transform:
            translateY(0)
            scale(1);
        }

        .trade-card:hover {
          border-color:
            rgba(8, 127, 35, 0.22);

          box-shadow:
            0 25px 60px
            rgba(0, 0, 0, 0.12);

          transform:
            translateY(-8px)
            scale(1.005);
        }

        /* =========================================================
           IMAGE
        ========================================================= */

        .trade-image-area {
          position: relative;

          height: 390px;

          overflow: hidden;

          background:
            #e9ecea;
        }

        .trade-image {
          width: 100%;
          height: 100%;

          display: block;

          object-fit: cover;

          transition:
            transform 0.9s
              cubic-bezier(0.2, 0.7, 0.2, 1),
            filter 0.5s ease;
        }

        .trade-card:hover .trade-image {
          transform:
            scale(1.075);

          filter:
            saturate(1.08);
        }

        /* =========================================================
           IMAGE OVERLAY
        ========================================================= */

        .trade-image-overlay {
          position: absolute;

          inset: 0;

          background:
            linear-gradient(
              180deg,
              rgba(0, 0, 0, 0.02) 15%,
              rgba(0, 0, 0, 0.10) 50%,
              rgba(0, 0, 0, 0.68) 100%
            );

          pointer-events: none;
        }

        .trade-image-overlay::after {
          content: "";

          position: absolute;

          inset: 0;

          background:
            linear-gradient(
              120deg,
              rgba(8, 127, 35, 0.20),
              transparent 45%
            );

          opacity: 0;

          transition:
            opacity 0.45s ease;
        }

        .trade-card:hover
          .trade-image-overlay::after {
          opacity: 1;
        }

        /* =========================================================
           NUMBER
        ========================================================= */

        .trade-number {
          position: absolute;

          top: 20px;
          left: 20px;

          width: 52px;
          height: 52px;

          display: flex;
          align-items: center;
          justify-content: center;

          color:
            #111111;

          background:
            #f4c400;

          border-radius:
            50%;

          font-size: 12px;
          font-weight: 900;

          box-shadow:
            0 8px 20px
            rgba(0, 0, 0, 0.18);

          z-index: 3;

          transition:
            transform 0.35s ease;
        }

        .trade-card:hover
          .trade-number {
          transform:
            rotate(8deg)
            scale(1.08);
        }

        /* =========================================================
           IMAGE BOTTOM LABEL
        ========================================================= */

        .trade-image-caption {
          position: absolute;

          left: 25px;
          right: 25px;
          bottom: 24px;

          z-index: 3;

          color:
            #ffffff;

          transform:
            translateY(8px);

          transition:
            transform 0.35s ease;
        }

        .trade-card:hover
          .trade-image-caption {
          transform:
            translateY(0);
        }

        .trade-eyebrow {
          display: block;

          margin-bottom: 6px;

          color:
            #f4c400;

          font-size: 10px;
          font-weight: 800;

          letter-spacing: 1.5px;

          text-transform: uppercase;
        }

        .trade-image-title {
          margin: 0;

          color:
            #ffffff;

          font-size: 25px;
          font-weight: 800;

          line-height: 1.2;
        }

        /* =========================================================
           CONTENT
        ========================================================= */

        .trade-content {
          position: relative;

          padding:
            29px 31px 32px;
        }

        .trade-content::before {
          content: "";

          position: absolute;

          top: 0;
          left: 31px;

          width: 55px;
          height: 4px;

          background:
            linear-gradient(
              90deg,
              #087f23,
              #f4c400
            );

          border-radius:
            0 0 10px 10px;

          transition:
            width 0.35s ease;
        }

        .trade-card:hover
          .trade-content::before {
          width: 105px;
        }

        .trade-content-title {
          margin:
            3px 0 13px;

          color:
            #087f23;

          font-size: 21px;
          font-weight: 800;

          line-height: 1.25;
        }

        .trade-content-description {
          margin: 0;

          color:
            #69747a;

          font-size: 13.5px;

          line-height: 1.85;
        }

        /* =========================================================
           BOTTOM ACCENT
        ========================================================= */

        .trade-card-footer {
          margin-top: 23px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;
        }

        .trade-footer-line {
          flex: 1;

          height: 1px;

          background:
            #e8ece9;
        }

        .trade-footer-dot {
          width: 9px;
          height: 9px;

          flex-shrink: 0;

          background:
            #f4c400;

          border-radius: 50%;

          transition:
            transform 0.35s ease,
            background 0.35s ease;
        }

        .trade-card:hover
          .trade-footer-dot {
          background:
            #087f23;

          transform:
            scale(1.35);
        }

        /* =========================================================
           DESKTOP CARD ALTERNATING FEEL
        ========================================================= */

        .trade-card:nth-child(2)
          .trade-image-overlay {
          background:
            linear-gradient(
              180deg,
              rgba(0, 0, 0, 0.02) 15%,
              rgba(0, 0, 0, 0.10) 50%,
              rgba(8, 127, 35, 0.62) 100%
            );
        }

        /* =========================================================
           TABLET
        ========================================================= */

        @media (max-width: 900px) {

          .trade-section {
            padding:
              85px 18px 95px;
          }

          .trade-header {
            margin-bottom:
              48px;
          }

          .trade-grid {
            gap:
              25px;
          }

          .trade-image-area {
            height:
              320px;
          }

          .trade-image-title {
            font-size:
              21px;
          }

          .trade-content {
            padding:
              27px 25px 29px;
          }

          .trade-content::before {
            left:
              25px;
          }
        }

        /* =========================================================
           MOBILE
        ========================================================= */

        @media (max-width: 700px) {

          .trade-section {
            padding:
              70px 15px 80px;
          }

          .trade-background-circle {
            width:
              280px;

            height:
              280px;

            top:
              -120px;

            right:
              -120px;
          }

          .trade-background-circle-bottom {
            width:
              230px;

            height:
              230px;

            bottom:
              -120px;

            left:
              -110px;
          }

          .trade-header {
            margin-bottom:
              38px;
          }

          .trade-heading {
            font-size:
              clamp(29px, 8vw, 38px);

            letter-spacing:
              -1px;
          }

          .trade-heading-description {
            font-size:
              13px;

            line-height:
              1.75;
          }

          .trade-grid {
            grid-template-columns:
              1fr;

            max-width:
              620px;

            margin:
              0 auto;

            gap:
              25px;
          }

          .trade-image-area {
            height:
              300px;
          }

          .trade-content {
            padding:
              25px 23px 27px;
          }

          .trade-content::before {
            left:
              23px;
          }
        }

        /* =========================================================
           SMALL MOBILE
        ========================================================= */

        @media (max-width: 480px) {

          .trade-section {
            padding:
              60px 13px 70px;
          }

          .trade-label {
            padding:
              7px 13px;

            font-size:
              8px;

            letter-spacing:
              1.3px;
          }

          .trade-heading {
            font-size:
              28px;
          }

          .trade-heading-description {
            margin-top:
              17px;

            font-size:
              12.5px;
          }

          .trade-grid {
            gap:
              20px;
          }

          .trade-card {
            border-radius:
              16px;
          }

          .trade-image-area {
            height:
              245px;
          }

          .trade-number {
            top:
              15px;

            left:
              15px;

            width:
              44px;

            height:
              44px;
          }

          .trade-image-caption {
            left:
              18px;

            right:
              18px;

            bottom:
              18px;
          }

          .trade-eyebrow {
            font-size:
              8px;

            letter-spacing:
              1.2px;
          }

          .trade-image-title {
            font-size:
              19px;
          }

          .trade-content {
            padding:
              23px 19px 25px;
          }

          .trade-content::before {
            left:
              19px;
          }

          .trade-content-title {
            font-size:
              18px;
          }

          .trade-content-description {
            font-size:
              12.5px;

            line-height:
              1.75;
          }
        }

        /* =========================================================
           EXTRA SMALL
        ========================================================= */

        @media (max-width: 350px) {

          .trade-image-area {
            height:
              220px;
          }

          .trade-heading {
            font-size:
              25px;
          }

          .trade-content {
            padding:
              21px 17px 23px;
          }

          .trade-content::before {
            left:
              17px;
          }
        }

        /* =========================================================
           REDUCED MOTION
        ========================================================= */

        @media (prefers-reduced-motion: reduce) {

          .trade-header,
          .trade-card,
          .trade-image,
          .trade-number,
          .trade-image-caption,
          .trade-content::before,
          .trade-footer-dot {
            transition:
              none !important;
          }

          .trade-label::before {
            animation:
              none !important;
          }
        }
      `}</style>

      <section
        ref={sectionRef}
        className="trade-section"
      >
        <div className="trade-background-circle" />
        <div className="trade-background-circle-bottom" />

        <div className="trade-container">

          {/* =====================================================
              SECTION HEADER
          ===================================================== */}

          <div
            className={`trade-header ${
              visible ? "is-visible" : ""
            }`}
          >

            <span className="trade-label">
              Vocation & Trade Education
            </span>

            <h2 className="trade-heading">
              Skills That Create
              <span>Real Opportunities.</span>
            </h2>

            <p className="trade-heading-description">
              Our vocation and trade programs combine practical
              training, technical knowledge, creativity, and
              real-world experience to prepare students for
              employment, entrepreneurship, and a productive
              future.
            </p>

          </div>

          {/* =====================================================
              CARDS
          ===================================================== */}

          <div className="trade-grid">

            {vocationCards.map((item) => (

              <article
                key={item.id}
                className={`trade-card ${
                  visible ? "is-visible" : ""
                }`}
              >

                <div className="trade-image-area">

                  <img
                    src={item.image}
                    alt={item.title}
                    className="trade-image"
                  />

                  <div className="trade-image-overlay" />

                  <div className="trade-number">
                    {item.number}
                  </div>

                  <div className="trade-image-caption">

                    <span className="trade-eyebrow">
                      {item.eyebrow}
                    </span>

                    <h3 className="trade-image-title">
                      {item.title}
                    </h3>

                  </div>

                </div>

                <div className="trade-content">

                  <h3 className="trade-content-title">
                    {item.title}
                  </h3>

                  <p className="trade-content-description">
                    {item.description}
                  </p>

                  <div className="trade-card-footer">

                    <div className="trade-footer-line" />

                    <div className="trade-footer-dot" />

                  </div>

                </div>

              </article>

            ))}

          </div>

        </div>
      </section>
    </>
  );
};

export default Card;